import type { ReactNode } from 'react';
import { Box } from '@mui/material';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@/primitives';
import {
  EBV_TRAIT_COLUMN_HEADER_HEIGHT,
  categoryCellSx,
  categoryRowEdges,
  type EbvGroupingOptions,
} from '@/hooks/publicSearch/ebv/ebvColumns';
import BreedAverageEbvCell from '@/components/publicSearch/animal/ebv/BreedAverageEbvCell';
import { coerceBreedAverageRaw } from '@/components/publicSearch/animal/ebv/breedAverageDisplay';
import { EbvTraitHeaderLabel } from '@/components/publicSearch/animal/ebv/EbvTraitHeaderLabel';
import {
  formatEbvLabelPlain,
  type EbvTraitHeaderContext,
} from '@/components/publicSearch/animal/ebv/ebvTraitExplanations';
import TraitLeaderIcon from '@/components/publicSearch/animal/ebv/TraitLeaderIcon';
import { withTraitLeaderCellSx } from '@/components/publicSearch/animal/ebv/traitLeader';
import type { EbvAverageData, EbvRow } from './animalEbvTabTypes';

/**
 * Phone column widths. Five columns share roughly 300px. The trait column only
 * has to hold an initialism (GL, MCW, 2DW), so the value columns get the rest;
 * the table stretches these proportionally on wider phones.
 */
const MOBILE_LABEL_COLUMN_WIDTH = 73;
const MOBILE_VALUE_COLUMN_WIDTH = 57;

type AnimalEbvTabTraitTableProps = {
  isMobile: boolean;
  ebvRows: EbvRow[];
  showPercentileColumn: boolean;
  showAccuracyColumn: boolean;
  /** Left gutter (EBV / Acc / Percentile). Off for dairy values-only (i4 no `heading`). */
  showRowLabels: boolean;
  ebvLabel: string;
  accuracyHeader: ReactNode;
  /** Phone only: breed average as a column here rather than a second table. */
  showBreedAverageColumn?: boolean;
  ebvAverageData?: EbvAverageData | null;
  currencySymbol: string;
  traitHeaderContext: EbvTraitHeaderContext;
  ebvGroupingOptions?: EbvGroupingOptions;
};

function TraitLeaderValueCell({
  value,
  isTraitLeader,
}: {
  value: string;
  isTraitLeader: boolean;
}) {
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 28,
        px: isTraitLeader ? 1.25 : 0,
      }}
    >
      {isTraitLeader && (
        <TraitLeaderIcon
          sx={{
            position: 'absolute',
            top: 2,
            right: 0,
          }}
        />
      )}
      {value || '—'}
    </Box>
  );
}

export default function AnimalEbvTabTraitTable({
  isMobile,
  ebvRows,
  showPercentileColumn,
  showAccuracyColumn,
  showRowLabels,
  ebvLabel,
  accuracyHeader,
  showBreedAverageColumn = false,
  ebvAverageData,
  currencySymbol,
  traitHeaderContext,
  ebvGroupingOptions,
}: AnimalEbvTabTraitTableProps) {
  return (
    <Table
      size="small"
      sx={{
        minWidth: { xs: 'auto', md: 900 },
        width: isMobile ? '100%' : undefined,
        tableLayout: 'fixed',
        '& td:not(:first-of-type)': {
          whiteSpace: 'nowrap',
        },
        '& td:first-of-type': { whiteSpace: 'normal' },
        '& th': {
          whiteSpace: 'normal',
          lineHeight: 1.2,
          verticalAlign: 'bottom',
          minHeight: EBV_TRAIT_COLUMN_HEADER_HEIGHT,
          px: 0.75,
          py: 0.5,
        },
      }}
    >
      {isMobile ? (
        // Pivoted layout for small screens: one row per trait, with the breed
        // average alongside the animal's own value instead of in a second
        // table below (ILRX-4700). Short trait names keep all of it inside a
        // phone's width without sideways scrolling.
        <>
          <colgroup>
            <col style={{ width: MOBILE_LABEL_COLUMN_WIDTH }} />
            <col style={{ width: MOBILE_VALUE_COLUMN_WIDTH }} />
            {showAccuracyColumn && (
              <col style={{ width: MOBILE_VALUE_COLUMN_WIDTH }} />
            )}
            {showPercentileColumn && (
              <col style={{ width: MOBILE_VALUE_COLUMN_WIDTH }} />
            )}
            {showBreedAverageColumn && (
              <col style={{ width: MOBILE_VALUE_COLUMN_WIDTH }} />
            )}
          </colgroup>
          {(showRowLabels ||
            showAccuracyColumn ||
            showPercentileColumn ||
            showBreedAverageColumn) && (
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell align="center">
                  {showRowLabels ? (
                    <Typography
                      variant="bodysmall"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {ebvLabel}
                    </Typography>
                  ) : null}
                </TableCell>
                {showAccuracyColumn && (
                  <TableCell align="center">{accuracyHeader}</TableCell>
                )}
                {showPercentileColumn && (
                  <TableCell align="center">
                    <Typography
                      variant="bodysmall"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      %ile
                    </Typography>
                  </TableCell>
                )}
                {showBreedAverageColumn && (
                  <TableCell align="center">
                    <Typography
                      variant="bodysmall"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      Avg
                    </Typography>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {ebvRows.map((ebvRow, i) => {
              // Tint each trait row by its category (top
              // border at the start of each category block)
              // to match the grid / mating predictor.
              const { isFirst } = categoryRowEdges(ebvRows, i);
              const cellSx = categoryCellSx(
                ebvRow.category,
                { top: isFirst },
                ebvGroupingOptions,
              );
              return (
                <TableRow key={ebvRow.key} hover>
                  <TableCell
                    sx={cellSx}
                    title={formatEbvLabelPlain(ebvRow.label)}
                  >
                    <EbvTraitHeaderLabel
                      label={ebvRow.shortLabel}
                      traitKey={ebvRow.traitKey}
                      traitHeaderContext={traitHeaderContext}
                      typographyProps={{ color: 'text.secondary' }}
                    />
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={withTraitLeaderCellSx(cellSx, ebvRow.isTraitLeader)}
                  >
                    <TraitLeaderValueCell
                      value={ebvRow.value}
                      isTraitLeader={ebvRow.isTraitLeader}
                    />
                  </TableCell>
                  {showAccuracyColumn && (
                    <TableCell align="center" sx={cellSx}>
                      {ebvRow.accuracy || '—'}
                    </TableCell>
                  )}
                  {showPercentileColumn && (
                    <TableCell align="center" sx={cellSx}>
                      {ebvRow.percentile || '—'}
                    </TableCell>
                  )}
                  {showBreedAverageColumn && (
                    <TableCell align="center" sx={cellSx}>
                      <BreedAverageEbvCell
                        raw={coerceBreedAverageRaw(
                          ebvAverageData?.[ebvRow.avgKey],
                        )}
                        format={ebvRow.format}
                        currencySymbol={currencySymbol}
                      />
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </>
      ) : (
        // Original layout for larger screens
        <>
          <colgroup>
            {showRowLabels && <col style={{ width: 140 }} />}
            {ebvRows.map((ebvRow) => (
              <col key={ebvRow.key} style={{ width: 86 }} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              {showRowLabels && <TableCell />}
              {ebvRows.map((ebvRow, i) => {
                const { isFirst, isLast } = categoryRowEdges(ebvRows, i);
                return (
                  <TableCell
                    key={ebvRow.key}
                    align="center"
                    sx={categoryCellSx(
                      ebvRow.category,
                      {
                        left: isFirst,
                        right: isLast,
                        top: true,
                        header: true,
                      },
                      ebvGroupingOptions,
                    )}
                  >
                    <EbvTraitHeaderLabel
                      label={ebvRow.label}
                      traitKey={ebvRow.traitKey}
                      traitHeaderContext={traitHeaderContext}
                    />
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow hover>
              {showRowLabels && (
                <TableCell>
                  <Typography
                    variant="bodysmall"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {ebvLabel}
                  </Typography>
                </TableCell>
              )}
              {ebvRows.map((ebvRow, i) => {
                const { isFirst, isLast } = categoryRowEdges(ebvRows, i);
                return (
                  <TableCell
                    key={ebvRow.key}
                    align="center"
                    sx={withTraitLeaderCellSx(
                      categoryCellSx(
                        ebvRow.category,
                        { left: isFirst, right: isLast },
                        ebvGroupingOptions,
                      ),
                      ebvRow.isTraitLeader,
                    )}
                  >
                    <TraitLeaderValueCell
                      value={ebvRow.value}
                      isTraitLeader={ebvRow.isTraitLeader}
                    />
                  </TableCell>
                );
              })}
            </TableRow>

            {showAccuracyColumn && (
              <TableRow hover>
                {showRowLabels && <TableCell>{accuracyHeader}</TableCell>}
                {ebvRows.map((ebvRow, i) => {
                  const { isFirst, isLast } = categoryRowEdges(ebvRows, i);
                  return (
                    <TableCell
                      key={ebvRow.key}
                      align="center"
                      sx={categoryCellSx(
                        ebvRow.category,
                        { left: isFirst, right: isLast },
                        ebvGroupingOptions,
                      )}
                    >
                      {ebvRow.accuracy || '—'}
                    </TableCell>
                  );
                })}
              </TableRow>
            )}

            {showPercentileColumn && (
              <TableRow hover>
                {showRowLabels && (
                  <TableCell>
                    <Typography
                      variant="bodysmall"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      Percentile
                    </Typography>
                  </TableCell>
                )}
                {ebvRows.map((ebvRow, i) => {
                  const { isFirst, isLast } = categoryRowEdges(ebvRows, i);
                  return (
                    <TableCell
                      key={ebvRow.key}
                      align="center"
                      sx={categoryCellSx(
                        ebvRow.category,
                        { left: isFirst, right: isLast },
                        ebvGroupingOptions,
                      )}
                    >
                      {ebvRow.percentile || '—'}
                    </TableCell>
                  );
                })}
              </TableRow>
            )}
          </TableBody>
        </>
      )}
    </Table>
  );
}
